"use client";

import { createContext, FunctionComponent, PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useRouter as useNextRouter } from "next/router";
import { remToPx } from "../utils/helper";
import { buildFilterString, filtersEqual, parseFilterString, sortUnique } from "./router-provider.utils";
import { GQLChannel, GQLPostType } from "graphql.generated";

export type FilterKey = "ch" | "pt" | "in" | "ht" | "tab";

export type FilterValueMap = {
    ch: GQLChannel;
    pt: GQLPostType;
    in: string;
    ht: string;
    tab: string;
};

export type Filters = {
    ch?: GQLChannel[];
    pt?: GQLPostType[];
    in?: string[];
    ht?: string[];
    tab?: string;
};

type ModifyFilterFn = <K extends FilterKey>(type: K, add?: FilterValueMap[K][], remove?: FilterValueMap[K][], raw?: boolean) => string;

type ToggleFilterFn = <K extends FilterKey>(type: K, value?: FilterValueMap[K] | null, raw?: boolean) => string;

type ClearFilterTypeFn = <K extends FilterKey>(type: K, raw?: boolean) => string;

export interface RouterContextType {
    filters: Filters;
    filterString: string;
    modifyFilter: ModifyFilterFn;
    toggleFilter: ToggleFilterFn;
    clearFilterType: ClearFilterTypeFn;
    hash: string | null;
    anchorScroll: (url: string, behavior?: ScrollBehavior, setHash?: boolean, delay?: number) => void;
}

export const RouterContext = createContext<RouterContextType | undefined>(undefined);

export const RouterProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const router = useNextRouter();
    const [filters, setFilters] = useState<Filters>({});
    const [hash, setHash] = useState<string | null>(() => (typeof window !== "undefined" ? window.location.hash.replace(/[#/]/g, "") || null : null));

    useEffect(() => {
        if (!router.isReady) return;
        const raw = typeof router.query.filter === "string" ? router.query.filter : "";
        const parsed = parseFilterString(raw);
        setFilters((prev) => (filtersEqual(prev, parsed) ? prev : parsed));
    }, [router.isReady, router.query.filter]);

    useEffect(() => {
        const fn = () => setHash(window.location.hash.replace(/[#/]/g, "") || null);
        fn();
        window.addEventListener("hashchange", fn, false);
        return () => window.removeEventListener("hashchange", fn, false);
    }, []);

    const buildFromFilters = useCallback(
        (next: Filters, raw = false): string => {
            const str = buildFilterString(next);
            if (raw) return str;
            const q = new URLSearchParams(
                Object.entries(router.query).reduce<Record<string, string>>((o, [k, v]) => {
                    if (k !== "filter" && typeof v === "string") o[k] = v;
                    return o;
                }, {}),
            );
            str ? q.set("filter", str) : q.delete("filter");
            const qs = q.toString();
            return `${router.pathname}${qs ? "?" + qs : ""}`;
        },
        [router.pathname, router.query],
    );

    const modifyFilter: ModifyFilterFn = useCallback(
        <K extends FilterKey>(type: K, add: FilterValueMap[K][] = [], remove: FilterValueMap[K][] = [], raw = false) => {
            const next: Filters = { ...filters };
            if (type === "tab") {
                const addVal = add[0];
                const removeVal = remove[0];
                const currentVal = filters.tab;
                if (addVal && addVal !== currentVal) {
                    next.tab = addVal as string;
                } else if (removeVal && removeVal === currentVal) {
                    delete next.tab;
                }
                return buildFromFilters(next, raw);
            }
            const current = (filters[type] ?? []) as unknown as string[];
            const set = new Set(current);
            (add as unknown as string[]).forEach((v) => set.add(v));
            (remove as unknown as string[]).forEach((v) => set.delete(v));
            const arr = sortUnique(Array.from(set)) as FilterValueMap[K][];
            if (arr.length) {
                (next as any)[type] = arr;
            } else {
                delete next[type];
            }
            return buildFromFilters(next, raw);
        },
        [filters, buildFromFilters],
    );

    const clearFilterType: ClearFilterTypeFn = useCallback(
        (type, raw = false) => {
            const next: Filters = { ...filters };
            delete next[type];
            return buildFromFilters(next, raw);
        },
        [filters, buildFromFilters],
    );

    const toggleFilter: ToggleFilterFn = useCallback(
        (type, value, raw = false) => {
            const current = filters[type];

            if (value == null) {
                return clearFilterType(type, raw);
            }
            const strValue = value as unknown as string;
            const exists = Array.isArray(current) ? (current as unknown as string[]).includes(strValue) : (current as unknown) === value;
            return modifyFilter(type, exists ? [] : [value], exists ? [value] : [], raw);
        },
        [filters, modifyFilter, clearFilterType],
    );

    const anchorScroll = useCallback((url: string, behavior: ScrollBehavior = "auto", setHashParam = false, delay = 1) => {
        const { origin, pathname, search } = window.location;
        let absUrl = url;
        try {
            absUrl = new URL(url).toString();
        } catch {
            absUrl = url.startsWith("/") ? origin + url : origin + pathname + search + url;
        }
        const urlObj = new URL(absUrl);
        const samePage = urlObj.origin + urlObj.pathname + urlObj.search === origin + pathname + search;
        if (!samePage && setHashParam) {
            window.location.href = absUrl;
            return;
        }
        setTimeout(() => {
            const site = document.getElementsByClassName("site")[0] as HTMLElement;
            const finalHash = urlObj.hash.replace(/[#/]/g, "");
            const el = document.getElementById(finalHash);
            if (!el) return;
            const offNav = getComputedStyle(site).getPropertyValue("--offset-navigation-scrolled");
            const offset = offNav.includes("rem") ? remToPx(offNav) : parseInt(offNav || "0", 10);
            const y = el.offsetTop - (offset + parseInt(getComputedStyle(el).marginTop || "0", 10));
            if (behavior === "smooth") {
                window.scrollTo({ top: y, behavior });
            } else {
                const id = setInterval(() => window.scrollTo({ top: y }), 10);
                setTimeout(() => clearInterval(id), 500);
            }
            if (setHashParam) {
                history.pushState(null, "", `#${finalHash}`);
                window.dispatchEvent(
                    new HashChangeEvent("hashchange", {
                        oldURL: window.location.href,
                        newURL: window.location.href,
                    }),
                );
            }
        }, delay);
    }, []);

    const value = useMemo(
        () => ({
            filters,
            filterString: buildFilterString(filters),
            modifyFilter,
            toggleFilter,
            clearFilterType,
            hash,
            anchorScroll,
        }),
        [filters, modifyFilter, toggleFilter, clearFilterType, hash, anchorScroll],
    );

    return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
};
